/**
 * Notebooks API Service
 *
 * Provides API access for interactive Python notebooks.
 */
import api from './api';

// =============================================================================
// Types
// =============================================================================

export interface Notebook {
  id: string;
  name: string;
  description?: string;
  is_archived: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
  cell_count: number;
  has_active_session: boolean;
}

export interface NotebookDetail extends Notebook {
  capabilities: Record<string, unknown>;
  cells: NotebookCell[];
  active_session?: NotebookSession;
}

export interface NotebookCell {
  id: string;
  notebook_id: string;
  cell_type: 'code' | 'markdown';
  source: string;
  outputs: CellOutput[];
  execution_count?: number;
  execution_status?: 'pending' | 'running' | 'success' | 'error';
  position: number;
  created_at: string;
  updated_at: string;
}

export interface CellOutput {
  output_type: 'execute_result' | 'stream' | 'error' | 'display_data';
  data?: Record<string, string>;
  name?: string;
  text?: string;
  ename?: string;
  evalue?: string;
  traceback?: string[];
  metadata?: Record<string, unknown>;
}

export interface NotebookSession {
  id: string;
  notebook_id: string;
  status: 'starting' | 'ready' | 'busy' | 'idle' | 'stopped' | 'error';
  pyodide_session_id?: string;
  started_by: string;
  memory_usage_mb: number;
  execution_count: number;
  started_at: string;
  last_activity_at: string;
}

export interface ExecuteResponse {
  cell_id: string;
  execution_count: number;
  outputs: CellOutput[];
  execution_time_ms: number;
  status: 'success' | 'error';
}

export interface CreateNotebookRequest {
  name: string;
  description?: string;
  capabilities?: Record<string, unknown>;
}

export interface UpdateNotebookRequest {
  name?: string;
  description?: string;
  capabilities?: Record<string, unknown>;
  is_archived?: boolean;
}

export interface CreateCellRequest {
  cell_type?: 'code' | 'markdown';
  source?: string;
  position?: number;
}

export interface UpdateCellRequest {
  cell_type?: 'code' | 'markdown';
  source?: string;
}

// =============================================================================
// Notebook CRUD
// =============================================================================

/**
 * List all notebooks
 */
export async function listNotebooks(
  includeArchived: boolean = false,
  limit: number = 50,
  offset: number = 0
): Promise<Notebook[]> {
  const response = await api.get('/notebooks', {
    params: { include_archived: includeArchived, limit, offset },
  });
  return response.data;
}

/**
 * Get a notebook with cells and session info
 */
export async function getNotebook(notebookId: string): Promise<NotebookDetail> {
  const response = await api.get(`/notebooks/${notebookId}`);
  return response.data;
}

/**
 * Create a new notebook
 */
export async function createNotebook(
  request: CreateNotebookRequest
): Promise<NotebookDetail> {
  const response = await api.post('/notebooks', request);
  return response.data;
}

/**
 * Update a notebook
 */
export async function updateNotebook(
  notebookId: string,
  request: UpdateNotebookRequest
): Promise<NotebookDetail> {
  const response = await api.put(`/notebooks/${notebookId}`, request);
  return response.data;
}

/**
 * Delete a notebook
 */
export async function deleteNotebook(
  notebookId: string
): Promise<{ status: string; notebook_id: string }> {
  const response = await api.delete(`/notebooks/${notebookId}`);
  return response.data;
}

// =============================================================================
// Cell Management
// =============================================================================

/**
 * Create a new cell
 */
export async function createCell(
  notebookId: string,
  request: CreateCellRequest
): Promise<NotebookCell> {
  const response = await api.post(`/notebooks/${notebookId}/cells`, request);
  return response.data;
}

/**
 * Update a cell
 */
export async function updateCell(
  notebookId: string,
  cellId: string,
  request: UpdateCellRequest
): Promise<NotebookCell> {
  const response = await api.put(
    `/notebooks/${notebookId}/cells/${cellId}`,
    request
  );
  return response.data;
}

/**
 * Delete a cell
 */
export async function deleteCell(
  notebookId: string,
  cellId: string
): Promise<{ status: string; cell_id: string }> {
  const response = await api.delete(
    `/notebooks/${notebookId}/cells/${cellId}`
  );
  return response.data;
}

/**
 * Reorder cells
 */
export async function reorderCells(
  notebookId: string,
  cellIds: string[]
): Promise<NotebookCell[]> {
  const response = await api.post(`/notebooks/${notebookId}/cells/reorder`, {
    cell_ids: cellIds,
  });
  return response.data;
}

// =============================================================================
// Session Management
// =============================================================================

/**
 * Start a new session
 */
export async function startSession(
  notebookId: string,
  capabilities?: Record<string, unknown>
): Promise<NotebookSession> {
  const response = await api.post(`/notebooks/${notebookId}/sessions`, {
    capabilities,
  });
  return response.data;
}

/**
 * Stop a session
 */
export async function stopSession(
  notebookId: string,
  sessionId: string
): Promise<{ status: string; session_id: string }> {
  const response = await api.delete(
    `/notebooks/${notebookId}/sessions/${sessionId}`
  );
  return response.data;
}

/**
 * Restart a session
 */
export async function restartSession(
  notebookId: string,
  sessionId: string
): Promise<NotebookSession> {
  const response = await api.post(
    `/notebooks/${notebookId}/sessions/${sessionId}/restart`
  );
  return response.data;
}

// =============================================================================
// Execution
// =============================================================================

/**
 * Execute a cell
 */
export async function executeCell(
  notebookId: string,
  cellId: string,
  source?: string
): Promise<ExecuteResponse> {
  const response = await api.post(
    `/notebooks/${notebookId}/cells/${cellId}/execute`,
    source ? { source } : {}
  );
  return response.data;
}
